/** Admin-driven MFA enrollment dialog (TOTP QR / SMS / Email → confirm code). */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as usersApi from '@/api/users';
import type { MFADevice } from '@/api/users';
import { useSnackbar } from '@/components/Snackbar';

interface Props {
  userId: number;
  kind: MFADevice['kind'];
  defaultEmail?: string;
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}

export function MfaEnrollDialog({ userId, kind, defaultEmail, open, onClose, onEnrolled }: Props) {
  const snackbar = useSnackbar();
  const [phase, setPhase] = useState<'start' | 'confirm'>('start');
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [target, setTarget] = useState(kind === 'email' ? (defaultEmail ?? '') : '');
  const [code, setCode] = useState('');
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startMutation = useMutation({
    mutationFn: async () => {
      if (kind === 'totp') return usersApi.mfaEnrollTotp(userId);
      if (kind === 'sms') return usersApi.mfaEnrollSms(userId, target);
      return usersApi.mfaEnrollEmail(userId, target || undefined);
    },
    onSuccess: (data) => {
      setDeviceId(data.device_id);
      const totp = data as { qr_svg_b64?: string; secret?: string };
      if (totp.qr_svg_b64) {
        setQrSvg(totp.qr_svg_b64);
        setSecret(totp.secret ?? null);
      }
      setPhase('confirm');
      setError(null);
    },
    onError: (e: unknown) =>
      setError(e instanceof ConsoleApiError ? e.message : 'Could not start enrollment.'),
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      if (deviceId === null) throw new Error('No device.');
      if (kind === 'totp') return usersApi.mfaEnrollTotpConfirm(userId, deviceId, code);
      if (kind === 'sms') return usersApi.mfaEnrollSmsConfirm(userId, deviceId, code);
      return usersApi.mfaEnrollEmailConfirm(userId, deviceId, code);
    },
    onSuccess: () => {
      snackbar.show('MFA device enrolled.', 'success');
      onEnrolled();
    },
    onError: (e: unknown) =>
      setError(e instanceof ConsoleApiError ? e.message : 'Invalid code.'),
  });

  const title = { totp: 'Enroll authenticator (TOTP)', sms: 'Enroll SMS device', email: 'Enroll email device' }[kind];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {phase === 'start' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {kind === 'sms' && (
              <TextField
                label="Phone (E.164)"
                placeholder="+15551234567"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                fullWidth
              />
            )}
            {kind === 'email' && (
              <TextField
                label="Email"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                fullWidth
              />
            )}
            {kind === 'totp' && (
              <Typography variant="body2" color="text.secondary">
                A new authenticator secret + QR code will be generated for the user to scan.
              </Typography>
            )}
          </Stack>
        )}

        {phase === 'confirm' && (
          <Stack spacing={2} sx={{ mt: 1 }} alignItems="center">
            {kind === 'totp' && qrSvg && (
              <>
                <Box
                  component="img"
                  src={`data:image/svg+xml;base64,${qrSvg}`}
                  alt="TOTP QR code"
                  sx={{ width: 200, height: 200 }}
                />
                {secret && (
                  <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                    Manual key: <code>{secret}</code>
                  </Typography>
                )}
              </>
            )}
            {kind !== 'totp' && (
              <Typography variant="body2" color="text.secondary">
                A verification code was sent. Enter it below to confirm.
              </Typography>
            )}
            <TextField
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
              autoFocus
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {phase === 'start' ? (
          <Button
            variant="contained"
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending || (kind === 'sms' && !target)}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => confirmMutation.mutate()}
            disabled={!code || confirmMutation.isPending}
          >
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
